import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Search, Plus, SlidersHorizontal } from "lucide-react";
import { useDebounce } from "../../hooks/useDebounce";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import ScoutInfoDot from "../../components/scout-request/ScoutInfoDot";
import WeekStrip from "../../components/friendlies/WeekStrip";
import FriendlyComposer from "../../components/friendlies/FriendlyComposer";
import FriendlyFilterBar from "../../components/friendlies/FriendlyFilterBar";
import FriendlyFilterSheet from "../../components/friendlies/FriendlyFilterSheet";
import FriendlyCard from "../../components/friendlies/FriendlyCard";
import ApplyModal from "../../components/friendlies/ApplyModal";
import ShareActionSheet from "../../components/common/ShareActionSheet";
import { useFriendliesFeed } from "../../hooks/friendlies/useFriendliesFeed";
import { friendliesService } from "../../services/friendliesApi";
import { searchService } from "../../services/api";
import { groupByDate, bucketToRange, activeBucketKey } from "../../utils/friendlies";

// Build the friendly_request share_card (design Frame 9 / D-003) from a feed card,
// so ShareActionSheet drops a 'friendly_request' attachment the chat unfurl renders.
const buildShareCard = (r) => ({
  type: "friendly_request",
  id: r.id,
  club: r.club,
  team_category: r.team_category_label,
  team_label: r.team_label || "",
  date_phrase: r.date?.phrase,
  location: r.location,
  tier: r.tier?.display,
  status: r.status,
  is_open: r.status === "open",
  camp_multiple_matches: !!r.camp_multiple_matches,
});

// TP-Friendlies BROWSE (M2). Date-native: the server sorts soonest-first, the week
// strip filters by date bucket, and the list carries sticky date-group headers.
// Club-only (route-gated to ClubAdmin/Staff/Coach; agents are excluded API-wide).
const CardSkeleton = () => (
  <div className="bg-white rounded-[12px] p-4 border border-[#E6EAF0] animate-pulse flex gap-3">
    <div className="w-[86px] flex-shrink-0 border-r border-[#EEF1F5] pr-3">
      <div className="h-3 w-8 bg-[#E9EEF4] rounded mb-2" />
      <div className="h-6 w-10 bg-[#E9EEF4] rounded" />
    </div>
    <div className="flex-1">
      <div className="flex items-center gap-2.5 mb-3">
        <div className="w-9 h-9 rounded-full bg-[#E9EEF4]" />
        <div className="flex-1">
          <div className="h-3.5 w-32 bg-[#E9EEF4] rounded mb-1.5" />
          <div className="h-3 w-20 bg-[#E9EEF4] rounded" />
        </div>
      </div>
      <div className="h-4 w-48 bg-[#E9EEF4] rounded-full" />
    </div>
    <div className="w-[112px] flex flex-col items-end justify-between">
      <div className="w-5 h-5 bg-[#E9EEF4] rounded" />
      <div className="h-9 w-full bg-[#E9EEF4] rounded-lg" />
    </div>
  </div>
);

const FriendliesPage = () => {
  const navigate = useNavigate();
  const user = useSelector((s) => s.auth.user);
  const {
    requests,
    count,
    buckets,
    loading,
    error,
    filters,
    savedOnly,
    setFilter,
    setDateRange,
    removeFilter,
    clearFilters,
    toggleSavedOnly,
    toggleSaved,
    markApplied,
    hasActiveFilters,
  } = useFriendliesFeed();

  const [countries, setCountries] = useState([]);
  const [savedCount, setSavedCount] = useState(0);
  const [applyFor, setApplyFor] = useState(null);
  const [q, setQ] = useState("");
  const [composerOpen, setComposerOpen] = useState(false);
  const [shareFor, setShareFor] = useState(null);
  const isNarrow = useMediaQuery("(max-width: 640px)");
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);
  const debouncedQ = useDebounce(q, 350);
  useEffect(() => {
    setFilter("q", debouncedQ.trim());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQ]);

  useEffect(() => {
    let active = true;
    searchService.getCountries("").then((r) => active && r.success && setCountries(r.data || [])).catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  const refreshSavedCount = () =>
    friendliesService.getSaved().then((r) => r.success && setSavedCount(r.count)).catch(() => {});
  useEffect(() => {
    refreshSavedCount();
  }, []);

  const onToggleSave = (request) => {
    toggleSaved(request);
    // Refresh the authoritative Saved count shortly after the optimistic flip.
    setTimeout(refreshSavedCount, 250);
  };

  const onOpenChat = (request) => {
    const cid = request.my_application?.channel_cid;
    navigate(cid ? `/inbox?channel=${cid}` : "/inbox");
  };

  const activeKey = useMemo(
    () => activeBucketKey(filters.date_from, filters.date_to, buckets),
    [filters.date_from, filters.date_to, buckets],
  );
  const onSelectBucket = (bucket) => {
    const { date_from, date_to } = bucketToRange(bucket);
    setDateRange(date_from, date_to);
  };

  const groups = useMemo(() => groupByDate(requests), [requests]);

  return (
    // Platform-standard container (W1-04): no fixed max-width, page scales
    // to 2560+ via the progressive padding ladder.
    <div className="mx-auto px-4 md:px-6 lg:px-7 xl:px-8 min-[1920px]:px-10 min-[2560px]:px-12 py-6">
      <p className="text-[12.5px] text-[#8A98AC] mb-2">Marketplace / Friendlies</p>

      <div className="flex flex-col sm:flex-row items-start justify-between gap-3 mb-1">
        <h1 className="flex items-center gap-2 text-[26px] font-bold text-[#14263D]">
          Friendlies
          <ScoutInfoDot
            id="friendlies"
            title="How Friendlies works"
            body="Post when you need an opponent, or apply to another club's request with one tap. Everything is public under real club names. Agents aren't part of this area. Interested? Take it straight to your normal club-to-club chat; requests close on their own after the match date."
          />
        </h1>
        <div className="flex items-center flex-wrap gap-2 flex-shrink-0">
          <button onClick={() => navigate("/friendlies/applications")} className="px-3 min-h-[44px] sm:min-h-0 sm:py-2 rounded-lg border border-[#E4E9F1] text-[12.5px] font-semibold text-[#33475E] hover:bg-gray-50">My applications</button>
          <button onClick={() => navigate("/friendlies/mine")} className="px-3 min-h-[44px] sm:min-h-0 sm:py-2 rounded-lg border border-[#E4E9F1] text-[12.5px] font-semibold text-[#33475E] hover:bg-gray-50">Our requests</button>
          <button onClick={() => setComposerOpen(true)} className="inline-flex items-center gap-1.5 px-4 min-h-[44px] sm:min-h-0 sm:py-2 rounded-lg bg-[#2283FF] text-white text-[12.5px] font-semibold hover:bg-[#186FE0]">
            <Plus className="w-4 h-4" /> Post a friendly
          </button>
        </div>
      </div>
      <p className="text-[13.5px] text-[#5A6B82] mb-4">
        Public club-to-club matchmaking for friendly matches. Post yours, or apply in one tap.
      </p>

      {/* Search (BE ?q= over club name / context / team label / camp region) */}
      <div className="relative mb-4 max-w-md">
        <Search className="w-4 h-4 text-[#9AA8BC] absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search clubs, camp destinations…"
          className="w-full bg-white border border-[#E4E9F1] rounded-lg pl-9 pr-3 py-2 text-[13.5px] focus:outline-none focus:ring-2 focus:ring-[#2283FF]/20"
        />
      </div>

      {/* Week / date strip — the net-new date filter (per-bucket counts from meta) */}
      <div className="mb-4">
        <WeekStrip buckets={buckets} activeKey={activeKey} onSelect={onSelectBucket} totalCount={count} />
      </div>

      {/* Filters — desktop chip row; mobile collapses behind a bottom sheet. */}
      <div className="mb-5">
        <div className="hidden sm:block">
          <FriendlyFilterBar
            filters={filters}
            setFilter={setFilter}
            setDateRange={setDateRange}
            removeFilter={removeFilter}
            clearFilters={clearFilters}
            savedOnly={savedOnly}
            toggleSavedOnly={toggleSavedOnly}
            savedCount={savedCount}
            countries={countries}
            count={count}
            hasActiveFilters={hasActiveFilters}
          />
        </div>
        <button
          onClick={() => setFilterSheetOpen(true)}
          className="sm:hidden inline-flex items-center gap-1.5 rounded-full border border-[#E4E9F1] px-4 min-h-[44px] text-[13px] font-semibold text-[#33475E]"
        >
          <SlidersHorizontal className="w-4 h-4" />
          Filters
          {hasActiveFilters && <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-[#2283FF] text-white text-[10.5px] font-bold px-1">•</span>}
        </button>
      </div>

      {/* List / states */}
      {error ? (
        <div className="text-center py-16 rounded-2xl border border-dashed border-[#E4E9F1]">
          <p className="text-[15px] font-semibold text-[#14263D]">Couldn't load friendlies</p>
          <p className="text-[13px] text-[#8A98AC] mt-1">{error}</p>
        </div>
      ) : loading ? (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <CardSkeleton key={i} />
          ))}
        </div>
      ) : requests.length === 0 ? (
        <div className="text-center py-16 rounded-2xl border border-dashed border-[#E4E9F1]">
          <p className="text-[15px] font-semibold text-[#14263D]">
            {savedOnly ? "No saved friendlies yet" : "No friendlies match your filters"}
          </p>
          <p className="text-[13px] text-[#8A98AC] mt-1 max-w-md mx-auto">
            {savedOnly
              ? "Bookmark a request to clarify internally and apply later. Your saved ones show up here."
              : "Try a wider date range, or post your own request and let opponents come to you."}
          </p>
          {hasActiveFilters && (
            <button onClick={clearFilters} className="mt-3 text-[13px] font-semibold text-[#2283FF] hover:underline">
              Reset filters
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          {groups.map((g) => (
            <section key={g.key}>
              <div className="sticky top-0 z-10 -mx-4 px-4 py-2 bg-[#F6F8FB]/95 backdrop-blur-sm border-b border-[#EEF1F5] flex items-baseline gap-2">
                <span className="text-[11.5px] font-bold uppercase tracking-[0.06em] text-[#475467]">{g.label}</span>
                {g.sublabel && <span className="text-[11px] text-[#98A2B3]">· {g.sublabel}</span>}
                <span className="text-[11px] text-[#98A2B3]">({g.count})</span>
              </div>
              <div className="space-y-3 pt-3">
                {g.items.map((r) => (
                  <FriendlyCard
                    key={r.id}
                    request={r}
                    onApply={setApplyFor}
                    onToggleSave={onToggleSave}
                    onOpenChat={onOpenChat}
                    onShare={setShareFor}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      <ApplyModal
        request={applyFor}
        viewerClubName={user?.club_name}
        onClose={() => setApplyFor(null)}
        onApplied={(request) => markApplied(request.id)}
      />
      <FriendlyComposer
        open={composerOpen}
        onClose={() => setComposerOpen(false)}
        onPublished={() => {}}
        countries={countries}
        viewerClubName={user?.club_name}
      />
      <ShareActionSheet
        isOpen={!!shareFor}
        onClose={() => setShareFor(null)}
        title="Share friendly request"
        shareType="friendly_request"
        shareData={shareFor ? buildShareCard(shareFor) : {}}
      />
      <FriendlyFilterSheet
        open={isNarrow && filterSheetOpen}
        onClose={() => setFilterSheetOpen(false)}
        count={count}
        filters={filters}
        setFilter={setFilter}
        setDateRange={setDateRange}
        clearFilters={clearFilters}
        savedOnly={savedOnly}
        toggleSavedOnly={toggleSavedOnly}
        savedCount={savedCount}
        countries={countries}
      />
    </div>
  );
};

export default FriendliesPage;
